import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, NgModule, Optional } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CloudFunctions, ILicenseLabeling } from '@telespot/sdk';
import { environment } from '@telespot/shared/environment';
import { AuthService } from '@telespot/web-core';
import { map } from 'rxjs/operators';

type IfuLanguage = 'en' | 'es' | 'pt' | 'fr' | 'it';

interface LanguageOption {
  value: IfuLanguage;
  label: string;
}

/**
 * Which label layout to render, derived from the organization's license labeling.
 * `basic` is the safe fallback (no organization / no labeling): shows only manufacturer,
 * version and date — no regulatory marks, UDI or eIFU download.
 */
export type LabelVariant = 'ce' | 'legacy' | 'ruo' | 'basic';

@Component({
  selector: 'ts-about-dialog',
  templateUrl: './about-dialog.component.html',
  styleUrls: ['./about-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AboutDialogComponent {
  public readonly currentOrganization$ = this._authService.currentOrganization$;

  /** Label variant (CE / LEGACY / RUO); precedence CE > LEGACY > RUO, defaults to CE. */
  public readonly variant$ = this.currentOrganization$.pipe(
    map((organization) => this._resolveVariant(organization?.license?.labeling))
  );

  /** Software version. Acts as the serial number / UDI-PI. IVDR Annex I, Ch. III (f). */
  public readonly version: string = environment.VERSION;

  /**
   * Date of manufacture (release date). IVDR Annex I, Ch. III (i).
   * Derived from the last-modified date of the served document.
   */
  public readonly manufactureDate = new Date(this._document?.lastModified);

  /** GS1 UDI-DI for TELESPOT. Full systematics defined in [SPL-SOP-ITR]. */
  public readonly udiDi = '8437030011010';

  /** Notified Body number shown next to the CE mark (CE variant only). IVDR Annex V. */
  public readonly notifiedBody = '0318';

  /** Languages for which an eIFU PDF is published. */
  public readonly languages: LanguageOption[] = [
    { value: 'en', label: 'languages.english' },
    { value: 'es', label: 'languages.spanish' },
    { value: 'it', label: 'languages.italian' },
    { value: 'pt', label: 'languages.portuguese' },
    { value: 'fr', label: 'languages.french' },
  ];

  /** Language pre-selected for the eIFU download; defaults to the platform language. */
  public selectedLang: IfuLanguage = this._resolveInitialLanguage();

  /** True while a signed eIFU URL is being requested. */
  public loadingIfu = false;

  /**
   * Full UDI in Human Readable Interpretation (HRI) format (CE variant).
   * IVDR Annex VI, Part C: `(01)<UDI-DI>(11)<YYMMDD>(8012)<version>`.
   */
  public get udiHri(): string {
    return `(01)${this.udiDi}(11)${this.manufactureDatePi}(8012)${this.version}`;
  }

  /** Manufacture date formatted as YYMMDD for the UDI-PI production date (GS1 AI 11). */
  private get manufactureDatePi(): string {
    const d = this.manufactureDate;
    const yy = String(d.getFullYear()).slice(-2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}${mm}${dd}`;
  }

  /**
   * Requests a signed URL for the eIFU in the selected language and opens it.
   * The tab is opened synchronously first to avoid pop-up blockers, then redirected.
   */
  public async downloadIfu(variant: 'ce' | 'legacy', lang: IfuLanguage): Promise<void> {
    if (this.loadingIfu) return;

    const popup = this._document?.defaultView?.open('', '_blank');
    this.loadingIfu = true;
    this._cdr.markForCheck();

    try {
      const { url } = await CloudFunctions.GetIfuUrl({ variant, lang });
      if (popup) popup.location.href = url;
      else this._document?.defaultView?.open(url, '_blank');
    } catch (error) {
      popup?.close();
      console.error('[AboutDialog] Failed to fetch eIFU URL', error);
    } finally {
      this.loadingIfu = false;
      this._cdr.markForCheck();
    }
  }

  /**
   * Picks the label variant from the license labeling, with CE > LEGACY > RUO precedence.
   * Falls back to `basic` when there is no organization or no labeling (e.g. the public
   * landing page), so regulatory info and eIFU access are never exposed by default.
   */
  private _resolveVariant(labeling?: ILicenseLabeling[]): LabelVariant {
    const set = new Set(labeling ?? []);
    if (set.has(ILicenseLabeling.CE)) return 'ce';
    if (set.has(ILicenseLabeling.LEGACY)) return 'legacy';
    if (set.has(ILicenseLabeling.RUO)) return 'ruo';
    return 'basic';
  }

  /**
   * Resolves the pre-selected eIFU language to match the platform language, using the
   * same sources as the global language selector: the persisted user preference, then
   * the active TranslateService language, then the environment default.
   */
  private _resolveInitialLanguage(): IfuLanguage {
    const stored = this._document?.defaultView?.localStorage?.getItem('user_language');
    const candidates = [stored, this._translate?.currentLang, environment.default_language];

    const match = candidates.find((c) => this.languages.some((l) => l.value === c));

    return (match as IfuLanguage) ?? 'en';
  }

  constructor(
    @Optional() @Inject(DOCUMENT) private _document: Document,
    private readonly _authService: AuthService,
    private readonly _translate: TranslateService,
    private readonly _cdr: ChangeDetectorRef
  ) {}
}

@NgModule({
  imports: [CommonModule, TranslateModule, MatDialogModule],
  declarations: [AboutDialogComponent],
  exports: [AboutDialogComponent],
})
export class AboutDialogComponentModule {}
